// src/components/pages/ProductDetails.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CartProduct, AddIcon, MinusIcon } from "../../assets/icons/icons";
import { useTranslation } from "react-i18next";
import UseScrollAnimation from "../../hooks/UseScrollAnimation";
import RelatedItem from "../sections/RelatedItem";

axios.defaults.withCredentials = true;

const API_BASE = "https://laravel-api.aj-mart.com";
const buildStorageUrl = (path = "") =>
  `${API_BASE}/storage/app/public/${String(path || "").replace(/^\/+/, "")}`;

/* ========== Utils ========== */
const safeParse = (val, fallback) => {
  try {
    if (val == null) return fallback;
    if (Array.isArray(val) || typeof val === "object") return val;
    const parsed = JSON.parse(val);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
};

// تعقيم اللون ليصلح كـ CSS/سيرفر (يضيف # لو ناقص)
const sanitizeCssColor = (val) => {
  if (!val || typeof val !== "string") return "";
  const s = val.trim();
  if (/^(rgb|hsl)a?\(/i.test(s)) return s; // rgb/rgba/hsl
  if (/^[a-z]+$/i.test(s)) return s; // أسماء ألوان: red/black/...
  const hex = s.replace(/^#/, "");
  if (/^[0-9a-f]{3}$/i.test(hex) || /^[0-9a-f]{6}$/i.test(hex))
    return `#${hex}`;
  return "";
};

// هل النص HEX؟
const isHexString = (s) =>
  typeof s === "string" && /^#?[0-9a-f]{3}([0-9a-f]{3})?$/i.test(s.trim());

const normalizeCap = (s) =>
  String(s || "")
    .replace(/\s+/g, "")
    .toLowerCase();
const isZeroCapacity = (cap) => {
  const n = normalizeCap(cap);
  return n === "0" || n === "0gb" || n === "0g";
};

/** تطبيع مصادر الألوان إلى شكل موحّد: [{ id, name, hex }] */
const normalizeColors = (product, t) => {
  const srcAny = safeParse(product?.product_colors_with_hex, null);
  let list = [];

  if (Array.isArray(srcAny)) list = srcAny;
  else if (srcAny && typeof srcAny === "object") list = Object.values(srcAny);

  const normalizedFromWithHex = (list || [])
    .map((c) => {
      const id = c?.id ?? c?.color_id ?? c?.value_id ?? null;
      const rawName =
        c?.color_name ?? c?.name ?? c?.label ?? c?.title ?? c?.color ?? "";
      const rawHex = c?.color_hex ?? c?.hex ?? c?.code ?? c?.value ?? "";
      const name = String(rawName || rawHex || t("Color")).trim();
      const hex = sanitizeCssColor(rawHex);
      return { id, name, hex };
    })
    .filter((x) => x.name);

  if (normalizedFromWithHex.length) return normalizedFromWithHex;

  // fallback: أسماء فقط من product_color (Array أو CSV)
  const namesArr = safeParse(product?.product_color, []);
  const names = Array.isArray(namesArr)
    ? namesArr
    : String(product?.product_color || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

  if (names.length)
    return names.map((n, i) => ({ id: i + 1, name: n, hex: "" }));

  return [];
};

export default function ProductDetails() {
  const { t, i18n } = useTranslation();
  UseScrollAnimation();

  const { id } = useParams();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [id]);

  // state
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");
  const [product, setProduct] = useState(null);
  const [brand, setBrand] = useState(null);

  const [selectedImage, setSelectedImage] = useState(null);

  const [selectedColorIdx, setSelectedColorIdx] = useState(null);
  const [selectedCapacity, setSelectedCapacity] = useState(null);
  const [quantity, setQuantity] = useState(1);

  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupType, setPopupType] = useState("success");

  /* ====== Fetch Product ====== */
  useEffect(() => {
    let cancelled = false;

    const fetchProduct = async () => {
      setLoading(true);
      setLoadErr("");
      try {
        let prod;
        // جرّب endpoint المفرد
        try {
          const single = await axios.get(`${API_BASE}/api/products/${id}`);
          prod = single?.data?.product || single?.data?.data || null;
        } catch {
          // fallback: القائمة
          const list = await axios.get(`${API_BASE}/api/products`);
          const items = list?.data?.products || list?.data?.data || [];
          prod =
            items.find((it) => String(it.product_id) === String(id)) || null;
        }

        if (cancelled) return;

        if (!prod) {
          setLoadErr(t("Product not found"));
          setProduct(null);
          setLoading(false);
          return;
        }

        setProduct(prod);

        // الصور
        const imgs = safeParse(prod.product_images, []);
        const thumbs = [prod.product_main_img, ...imgs].filter(Boolean);
        setSelectedImage(thumbs[0] || prod.product_main_img || null);

        // البراند
        if (prod.brand_id) {
          try {
            const b = await axios.get(`${API_BASE}/api/brand/${prod.brand_id}`);
            if (!cancelled) setBrand(b?.data?.brand || null);
          } catch {
            /* ignore */
          }
        }
      } catch {
        if (!cancelled) setLoadErr(t("حدث خطأ أثناء جلب المنتج"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchProduct();
    return () => {
      cancelled = true;
    };
  }, [id, t]);

  /* ====== Derived ====== */
  // كل السعات كما تأتي من السيرفر
  const rawCapacities = useMemo(
    () => safeParse(product?.product_capacity, []),
    [product],
  );

  // فلترة "0 GB" من العرض
  const capacities = useMemo(
    () =>
      Array.isArray(rawCapacities)
        ? rawCapacities.filter((c) => !isZeroCapacity(c))
        : [],
    [rawCapacities],
  );

  const capacityPricesMap = useMemo(() => {
    const map = safeParse(product?.product_capacity_prices, {});
    const out = {};
    Object.keys(map || {}).forEach((k) => {
      const n = Number(map[k]);
      out[k] = Number.isFinite(n) ? n : null;
    });
    return out;
  }, [product]);

  // دالة تساعدنا نلقى سعر سعة بالـ normalize
  const getCapPrice = (cap) => {
    if (!cap) return null;
    const target = normalizeCap(cap);
    for (const [k, v] of Object.entries(capacityPricesMap)) {
      if (normalizeCap(k) === target && v != null) return Number(v);
    }
    return null;
  };

  // سعر 0 GB (للإخفاء لكن نستخدمه كافتراضي إن وجد)
  const zeroCapPrice = useMemo(() => {
    for (const [k, v] of Object.entries(capacityPricesMap)) {
      if (isZeroCapacity(k) && v != null) return Number(v);
    }
    return null;
  }, [capacityPricesMap]);

  const colors = useMemo(() => normalizeColors(product, t), [product, t]);
  const selectedColor =
    selectedColorIdx != null ? colors[selectedColorIdx] : null;

  // Auto-select أول لون إذا كان وحيدًا
  useEffect(() => {
    if (colors.length === 1 && selectedColorIdx == null) {
      setSelectedColorIdx(0);
    }
  }, [colors, selectedColorIdx]);

  const productName =
    i18n.language === "ar" ? product?.product_name_A : product?.product_name_E;
  const productDesc =
    i18n.language === "ar" ? product?.product_desc_A : product?.product_desc_E;
  const productFeat =
    i18n.language === "ar"
      ? product?.product_features_A
      : product?.product_features_E;

  // تقسيم السطور: يدعم \n الفعلية، "\\n" النصية، "\\ة" النصية، و"ة" نفسها
  const splitText = (txt) => {
    if (!txt) return [];
    return String(txt)
      .split(/(?:\r?\n|\\n|\\\u0629|\u0629)/g)
      .map((s) => s.trim())
      .filter(Boolean);
  };
  const descItems = splitText(productDesc);
  const featItems = splitText(productFeat);

  const thumbnails = useMemo(() => {
    const imgs = safeParse(product?.product_images, []);
    const arr = [product?.product_main_img, ...imgs].filter(Boolean);
    return [...new Set(arr)];
  }, [product]);

  // Pricing
  const discount = useMemo(
    () => Number(product?.product_price_discount ?? 0),
    [product],
  );

  // الأساس: سعة مختارة → سعرها، وإلا سعر 0 GB إن وُجد، وإلا product_price
  const basePrice = useMemo(() => {
    const prodPrice = Number(product?.product_price ?? 0);
    const chosenPrice = selectedCapacity ? getCapPrice(selectedCapacity) : null;
    if (chosenPrice != null) return chosenPrice;
    if (zeroCapPrice != null) return zeroCapPrice;
    return prodPrice;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, selectedCapacity, zeroCapPrice]);

  const finalPrice = useMemo(() => {
    const d = Math.max(0, Math.min(100, Number(discount || 0)));
    return Number((basePrice * (1 - d / 100)).toFixed(2));
  }, [basePrice, discount]);

  /* ====== Handlers ====== */
  const increaseQuantity = () => setQuantity((q) => q + 1);
  const decreaseQuantity = () => setQuantity((q) => (q > 1 ? q - 1 : 1));
  const handleCapacityClick = (cap) => setSelectedCapacity(cap);
  const handleColorClick = (idx) => setSelectedColorIdx(idx);

  // === محدث: نرسل selected_color_name و product_colors_with_hex للسلة ===
  const handleAddToCart = async () => {
    if (!product) return;

    if (colors.length > 0 && selectedColorIdx == null) {
      setPopupMessage(t("الرجاء اختيار اللون"));
      setPopupType("error");
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 2000);
      return;
    }

    // نحضّر اللون للإرسال
    const selectedColorHexSanitized = sanitizeCssColor(
      selectedColor?.hex || "",
    );
    const colorToSend =
      selectedColorHexSanitized ||
      (selectedColor?.name && !isHexString(selectedColor.name)
        ? selectedColor.name
        : "");

    const payload = {
      product_id: parseInt(product.product_id, 10),
      product_name_E: product.product_name_E,
      product_name_A: product.product_name_A,

      // السعر بعد الخصم
      product_price: finalPrice,
      product_price_original: Number(basePrice),
      product_price_discount: discount,

      product_stock_available: parseInt(
        product.product_stock_available ?? 0,
        10,
      ),

      // اللون (نبعث الاسم + القيمة)
      selected_color: colorToSend,
      selected_color_name: selectedColor?.name || "",
      selected_color_id: selectedColor?.id ?? "",

      // لا نرسل "0 GB" كمختارة — فقط نرسل فارغ إن ما اختار سعة
      selected_capacity: selectedCapacity || "",

      // مهم: ابعث الماب عشان الكارت/التشيك أوت يعرفوا يطلعوا الاسم من HEX
      product_colors_with_hex: product?.product_colors_with_hex || "",

      product_category: product.product_category || "",
      product_sub_category: product.product_sub_category || "",
      product_brand_name: product.product_brand_name || "",
      product_tag: product.product_tag || "",
      product_features_E: product.product_features_E || "",
      product_features_A: product.product_features_A || "",
      product_desc_E: product.product_desc_E || "",
      product_desc_A: product.product_desc_A || "",
      product_color: product.product_color || "",
      product_capacity: product.product_capacity || "",
      product_main_img: product.product_main_img,
      product_images: product.product_images || "",
      product_username: product.product_username || "",
      brand_id: product.brand_id || "",
      category_id: product.category_id || "",
      sub_category_id: product.sub_category_id || "",
      quantity,
    };

    try {
      const res = await axios.post(`${API_BASE}/cart/add`, payload, {
        withCredentials: true,
      });
      setPopupMessage(res?.data?.message || t("تمت الإضافة إلى السلة"));
      setPopupType("success");
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 2200);
    } catch (error) {
      setPopupMessage(
        error?.response?.data?.message ||
          t("حدث خطأ أثناء إضافة المنتج إلى السلة"),
      );
      setPopupType("error");
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3000);
    }
  };

  /* ====== UI States ====== */
  if (loading) {
    return (
      <section className="py-10">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="w-full h-[420px] bg-gray-100 animate-pulse rounded-lg" />
            <div className="space-y-4">
              <div className="h-6 w-3/4 bg-gray-100 animate-pulse rounded" />
              <div className="h-4 w-full bg-gray-100 animate-pulse rounded" />
              <div className="h-4 w-5/6 bg-gray-100 animate-pulse rounded" />
              <div className="h-10 w-40 bg-gray-100 animate-pulse rounded" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (loadErr || !product) {
    return (
      <div className="container py-10">
        <div className="text-red-600 font-semibold">
          {loadErr || t("Product not found")}
        </div>
      </div>
    );
  }

  const outOfStock = Number(product.product_stock_available) <= 0;

  // لاحظ: لو السعات كلها كانت "0 GB" فقط → capacities بعد الفلترة = [] → لن تُعرض السعات (هذا المطلوب)
  const requiresCapacity = Array.isArray(capacities) && capacities.length > 0;
  const requiresColor = Array.isArray(colors) && colors.length > 0;
  const disableAdd =
    outOfStock ||
    (requiresCapacity && !selectedCapacity) ||
    (requiresColor && selectedColorIdx == null);

  return (
    <>
      {/* Popup */}
      {showPopup && (
        <div className="fixed inset-0 flex items-center justify-center z-[60] bg-black/50 animate-fade-in">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-[350px] flex items-center gap-4">
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center text-white ${
                popupType === "success" ? "bg-green-500" : "bg-red-500"
              }`}
            >
              {popupType === "success" ? (
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              ) : (
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              )}
            </div>
            <div className="flex-1">
              <p
                className={`text-sm font-medium ${popupType === "success" ? "text-green-700" : "text-red-700"}`}
              >
                {popupMessage}
              </p>
            </div>
            <button
              onClick={() => setShowPopup(false)}
              className="text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <section className="product-details py-10">
        <div className="container">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* الصور */}
            <div className="lg:w-[40%]">
              <div className="sticky top-24">
                <div className="w-full h-[300px] border border-[#6663] rounded-lg overflow-hidden bg-white">
                  {selectedImage ? (
                    <img
                      src={buildStorageUrl(selectedImage)}
                      alt={productName}
                      className="w-full h-full object-contain hover:scale-[1.015] transition-transform"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-50" />
                  )}
                </div>

                {thumbnails.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {thumbnails.map((img, i) => (
                      <button
                        key={`${img}-${i}`}
                        onClick={() => setSelectedImage(img)}
                        className={`border rounded-md p-1 w-[64px] h-[64px] bg-white ${
                          selectedImage === img
                            ? "border-[#1485a6]"
                            : "border-[#6663]"
                        }`}
                        title={t("Change image")}
                        type="button"
                      >
                        <img
                          src={buildStorageUrl(img)}
                          alt=""
                          className="w-full h-full object-contain"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* التفاصيل */}
            <div className="lg:w-[60%] flex flex-col gap-6">
              <p className="text-sm text-[#6668]">
                {t(
                  "Shop from a wide range of electronics, mobile devices and accessories from aj mart",
                )}
              </p>

              <div className="flex items-center gap-3">
                {brand?.brand_img ? (
                  <img
                    className="w-[38px] h-[38px] object-contain"
                    src={buildStorageUrl(brand.brand_img)}
                    alt={brand.brand_name}
                  />
                ) : (
                  <div className="w-[38px] h-[38px] bg-gray-200 rounded" />
                )}
                {brand?.brand_name && (
                  <span className="text-sm text-gray-600">
                    {brand.brand_name}
                  </span>
                )}
              </div>

              <div className="flex flex-col gap-3">
                <h1 className="text-2xl font-bold leading-snug">
                  {productName}
                </h1>

                {featItems.length > 0 && (
                  <ul className="list-disc ms-5 text-sm text-[#666] space-y-1">
                    {featItems.map((f, i) => (
                      <li key={i}>{f}</li>
                    ))}
                  </ul>
                )}

                <div
                  className={`font-bold ${outOfStock ? "text-red-500" : "text-[#18AB00]"}`}
                >
                  {t(outOfStock ? "Out of Stock" : "In Stock")}
                </div>
              </div>

              {/* السعات (0 GB مخفية) */}
              {capacities.length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  {capacities.map((cap, i) => (
                    <button
                      key={`${cap}-${i}`}
                      onClick={() => handleCapacityClick(cap)}
                      className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition ${
                        selectedCapacity === cap
                          ? "bg-[#1485a6] text-white border-[#1485a6]"
                          : "bg-[#6662] text-black border-transparent hover:bg-[#6663]"
                      }`}
                      title={cap}
                      type="button"
                    >
                      {cap}
                      {getCapPrice(cap) != null
                        ? ` • ${getCapPrice(cap)} ${t("JOD")}`
                        : ""}
                    </button>
                  ))}
                </div>
              )}

              {/* الألوان */}
              {colors.length > 0 && (
                <div className="space-y-2">
                  <div className="text-sm font-semibold">
                    {t("Colors")}
                    {selectedColor ? `: ${selectedColor.name}` : ""}
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    {colors.map((c, idx) => (
                      <button
                        key={`${c.name}-${idx}`}
                        onClick={() => handleColorClick(idx)}
                        className={`flex items-center gap-2 border rounded-full px-3 py-1 transition 
                          ${selectedColorIdx === idx ? "border-[#1485a6] ring-2 ring-[#1485a6]/20" : "border-transparent bg-black/5"}
                        `}
                        title={c.name}
                        type="button"
                      >
                        <span className="text-sm">{c.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* معلومات إضافية */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="font-semibold">{t("Discount")}:</span>{" "}
                  <span>{discount} %</span>
                </div>
                <div>
                  <span className="font-semibold">{t("Product Status")}:</span>{" "}
                  <span>{t(String(product.product_status || ""))}</span>
                </div>
              </div>

              {/* السعر + إضافة للسلة */}
              <div className="w-full sm:w-[380px] border border-[#0001] p-4 rounded-lg bg-white shadow-sm">
                <div className="flex justify-between items-center">
                  <div className="text-sm">{t("Delivery")}:</div>
                  <div className="bg-[rgb(13,166,120,0.12)] text-[#0DA678] font-bold px-3 py-0.5 rounded-full text-xs">
                    1 {t("JOD")}
                  </div>
                </div>

                <div className="flex flex-col items-center gap-1 my-5">
                  <span className="font-bold text-2xl">
                    {basePrice} {t("JOD")}
                  </span>
                  {discount > 0 && (
                    <span className="flex flex-col items-center text-red-500 text-sm">
                      {finalPrice} {t("JOD")}
                      <span>({t("After Discount")})</span>
                    </span>
                  )}
                </div>

                <div className="border border-[#0001] rounded-md flex items-center justify-between px-5 py-2">
                  <button
                    onClick={increaseQuantity}
                    className="p-1"
                    aria-label={t("Increase")}
                    type="button"
                  >
                    <AddIcon />
                  </button>
                  <span className="font-semibold">{quantity}</span>
                  <button
                    onClick={decreaseQuantity}
                    className="p-1"
                    aria-label={t("Decrease")}
                    type="button"
                  >
                    <MinusIcon />
                  </button>
                </div>

                {(() => {
                  const requiresCapacityNow = capacities.length > 0;
                  const requiresColorNow = colors.length > 0;
                  if (outOfStock) {
                    return (
                      <div className="text-xs text-amber-600 mt-2">
                        {t("المنتج غير متوفر حالياً")}
                      </div>
                    );
                  }
                  if (requiresCapacityNow && !selectedCapacity) {
                    return (
                      <div className="text-xs text-amber-600 mt-2">
                        {t("الرجاء اختيار السعة")}
                      </div>
                    );
                  }
                  if (requiresColorNow && selectedColorIdx == null) {
                    return (
                      <div className="text-xs text-amber-600 mt-2">
                        {t("الرجاء اختيار اللون")}
                      </div>
                    );
                  }
                  return null;
                })()}

                <button
                  onClick={handleAddToCart}
                  disabled={disableAdd}
                  className={`mt-3 w-full bg-[#1485a6] text-white font-bold rounded-md py-2.5 flex items-center justify-center gap-2 transition ${
                    disableAdd
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:opacity-90"
                  }`}
                  type="button"
                >
                  <CartProduct />
                  {t("Add To Cart")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {(descItems.length > 0 || !!productDesc) && (
        <section className="relative pb-10">
          <div className="container">
            <div className="text-[20px] font-bold capitalize px-5 py-3 border border-[#0001] rounded-t-lg w-fit text-[#1485a6] bg-white">
              {t("Description")}
            </div>
            <div className="p-5 border border-[#0001] rounded-b-lg bg-white">
              {descItems.length > 0 ? (
                <ul className="list-disc ms-6 text-sm text-[#666] space-y-1">
                  {descItems.map((d, i) => (
                    <li key={i}>{d}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-[#666]">{productDesc}</p>
              )}
            </div>
          </div>
        </section>
      )}
      <RelatedItem
        currentId={product.product_id}
        category={product.product_category}
      />
    </>
  );
}
